// src/pages/crypto.remedy.ts —— 2026-10-16 生产案例（U100005 漏付尾数）后的处置表单改造：入账用户按对外编号 UID 填写并预览、
// 处理方式 / 证据类型中文下拉、证据前缀由类型自动决定、凭证图片上传（迁移 0042；必传 1 张最多 3 张，复核前须逐张查看）、逐项报错。
// 本文件不 import crypto.ts（避免加载环）；表单定义仍在 crypto.ts（依赖其 cryptoErr）。
import { ApiError } from '../../../shared/src/api/client';
import type { AdminCtx } from '../domains/types';
import { str } from '../domains/types';
import { fmtDateTime } from '../../../shared/src/time';
import type { DrawerSpec, FormChange, FormVals } from './types';

export const I18N_REMEDY = {
  zh: {
    cpOwner: '入账用户（UID）', cpOwnerPh: 'U100005（也可填内部数字 ID）', cpOwnerPreview: '入账用户核对',
    cpOwnerNoPerm: '无「用户管理」查看权限，无法预览；提交时由服务器校验', cpOrder: '原订单号', cpGroup: '补款组号',
    cpEvidenceFiles: '凭证图片（必传 1 张，最多 3 张；jpg / png / webp，单张 ≤ 8MB）', cpEvidenceList: '已上传凭证',
    cpEvidenceNone: '尚未上传；请上传客户交易所提币截图、钱包签名截图等', cpEvidenceUploading: '正在上传 {n} 张…', cpEvidenceUploaded: '{n} 张：{list}',
    cpErrNote: '请填写处理说明', cpErrNoteLong: '处理说明不能超过 1000 字',
    cpErrEvidenceEmpty: '请上传至少 1 张凭证图片（客户交易所提币截图、钱包签名截图等）', cpErrEvidenceTooMany: '凭证图片最多 3 张；重新选择会替换已选图片',
    cpErrEvidenceType: '「{name}」不是 jpg / png / webp 图片', cpErrEvidenceSize: '「{name}」超过 8MB', cpErrEvidenceBusy: '上传过于频繁，请稍后再试',
    cpErrEvidenceRef: '凭证图片无效或不存在，请重新上传', cpErrEvidenceOwner: '只能引用自己上传的凭证图片，请重新上传', cpErrEvidenceReused: '该凭证图片已用于其他处置单，请重新上传本笔的凭证',
    cpViewEvidence: '查看凭证 #{id}', cpErrViewFirst: '请先在详情中点「查看凭证」逐张查看图片后再复核通过',
    cpErrOwnerEmpty: '请填写入账用户 UID（如 U100005）', cpErrOwnerFormat: '入账用户格式不正确：填 U 开头的 UID（如 U100005）或内部数字 ID',
    cpErrOwnerMissing: '未找到用户 {uid}，请核对 UID', cpErrOwnerInactive: '用户 {uid} 已封禁或已合并，不能入账（已合并请改填合并去向账号）',
    cpErrOwnerConflict: '入账用户 UID 与内部 ID 指向不同用户，请只填一项',
    cpErrOwnerMissingSrv: '服务器未找到该入账用户，请核对 UID', cpErrOwnerInactiveSrv: '入账用户已封禁或已合并，不能入账（已合并请改填合并去向账号）',
    cpErrOrderId: '原订单号须为纯数字（不填则不关联订单）', cpErrOrderOwner: '原订单不存在或不属于该入账用户，请核对订单号',
    cpErrGroupId: '补款合并入账须填写补款组号（纯数字；先在详情点「开补款组」）',
    cpErrRefundAddr: '退款地址格式不正确：TRC20 为 T 开头 34 位，ERC20/BEP20 为 0x 开头 40 位十六进制',
    cpUserSt_banned: '已封禁', cpUserSt_merged: '已合并', cpNoEmail: '无邮箱', cpNoTg: '无 TG',
    cpCandTitle: '候选订单（同网络、同收款地址、到账前 2 小时内未入账；按金额差排序）。点一行「选用」预填订单号与入账用户；凭哈希不能确认归属，仍须核实付款钱包签名或交易所提币凭证', cpCandEmpty: '无候选订单',
    cpCandDue: '应付 {amount} USDT', cpCandDiff: '差额 {diff}', cpCandPick: '点击选用', cpCandSystem: '系统匹配 · 点击选用', cpCandReadonly: '仅供核对（异常非待处理或无编辑权限）', cpCandCreated: '创建 {t}',
    cpRemedyScenes: '处理方式适用场景：① 漏付尾数 / 少付（如应付 100.001 实付 100）→ 核实归属后选「入账给用户（整笔）」，按实收整笔入账，不补差额；② 同一订单分多笔付款 → 先在详情点「开补款组」，再逐笔「补款合并入账」；③ 多付 / 重复付款 → 核实后整笔入账，或「原路退款」；④ 来源不明、无法证明归属 → 「原路退款」到客户提供并核实的地址，或「忽略」仅结案。',
  },
  en: {
    cpOwner: 'Credited user (UID)', cpOwnerPh: 'U100005 (internal numeric ID also accepted)', cpOwnerPreview: 'Credited user check',
    cpOwnerNoPerm: 'No Users view permission, so no preview; the server validates on submit', cpOrder: 'Original order No.', cpGroup: 'Supplement group No.',
    cpEvidenceFiles: 'Evidence images (1 required, up to 3; jpg / png / webp, ≤ 8MB each)', cpEvidenceList: 'Uploaded evidence',
    cpEvidenceNone: 'Nothing uploaded yet; upload the exchange withdrawal or wallet signature screenshots', cpEvidenceUploading: 'Uploading {n}…', cpEvidenceUploaded: '{n}: {list}',
    cpErrNote: 'Enter a review note', cpErrNoteLong: 'The review note cannot exceed 1000 characters',
    cpErrEvidenceEmpty: 'Upload at least 1 evidence image (exchange withdrawal or wallet signature screenshot)', cpErrEvidenceTooMany: 'Up to 3 evidence images; choosing again replaces the selection',
    cpErrEvidenceType: '"{name}" is not a jpg / png / webp image', cpErrEvidenceSize: '"{name}" exceeds 8MB', cpErrEvidenceBusy: 'Too many uploads; try again shortly',
    cpErrEvidenceRef: 'Evidence image invalid or missing; upload again', cpErrEvidenceOwner: 'You can only reference images you uploaded; upload again', cpErrEvidenceReused: 'This image is already used by another remedy; upload the evidence for this receipt',
    cpViewEvidence: 'View evidence #{id}', cpErrViewFirst: 'Open every evidence image (View evidence in the drawer) before approving',
    cpErrOwnerEmpty: 'Enter the credited user UID (e.g. U100005)', cpErrOwnerFormat: 'Invalid user: enter a UID starting with U (e.g. U100005) or an internal numeric ID',
    cpErrOwnerMissing: 'User {uid} not found. Check the UID', cpErrOwnerInactive: 'User {uid} is banned or merged and cannot be credited (for merged users, use the target account)',
    cpErrOwnerConflict: 'The UID and the internal ID point to different users. Fill in only one',
    cpErrOwnerMissingSrv: 'The server could not find this user. Check the UID', cpErrOwnerInactiveSrv: 'This user is banned or merged and cannot be credited (for merged users, use the target account)',
    cpErrOrderId: 'The original order No. must be numeric (leave empty for no order)', cpErrOrderOwner: 'The original order does not exist or does not belong to this user',
    cpErrGroupId: 'Supplement needs a numeric group No. (open a supplement group in the detail drawer first)',
    cpErrRefundAddr: 'Invalid refund address: TRC20 starts with T (34 chars); ERC20/BEP20 is 0x + 40 hex chars',
    cpUserSt_banned: 'banned', cpUserSt_merged: 'merged', cpNoEmail: 'no email', cpNoTg: 'no TG',
    cpCandTitle: 'Candidate orders (same network and deposit address, unpaid, created within 2 h before arrival; sorted by amount gap). Click a row to prefill order No. and user; a hash does not prove ownership, so still verify wallet or exchange evidence', cpCandEmpty: 'No candidate orders',
    cpCandDue: 'Due {amount} USDT', cpCandDiff: 'Gap {diff}', cpCandPick: 'Click to use', cpCandSystem: 'System match · click to use', cpCandReadonly: 'For reference (exception not open or no edit permission)', cpCandCreated: 'Created {t}',
    cpRemedyScenes: 'When to use each action: (1) short or missing decimals (e.g. due 100.001, paid 100) → after verifying ownership choose Credit user (full); the full received amount is credited and the gap is never invented; (2) one order paid in several transfers → open a supplement group in the drawer, then Supplement each receipt; (3) overpaid / duplicate payment → credit in full after verification, or Refund; (4) unknown source that cannot be attributed → Refund to a verified customer address, or Ignore to close only.',
  },
} as const;

const rText = (c: AdminCtx, k: string): string => str(c.t, k) || String(Reflect.get(I18N_REMEDY[c.lang], k) ?? k);
const fill = (s: string, vals: Readonly<Record<string, string>>): string => s.replace(/\{(\w+)\}/g, (m, k: string) => vals[k] ?? m);

/** 处理方式下拉（提交值不变）。 */
export const REMEDY_ACTIONS = ['credit', 'supplement', 'refund', 'ignore'] as const;
/** 证据类型 → evidence_refs 前缀（后端协议不变：`<前缀>:<编号>`）。 */
export const EVIDENCE_PREFIX: Readonly<Record<string, string>> = { wallet_signature: 'wallet-proof', exchange_receipt: 'exchange-receipt', verified_record: 'review-record' };
export const EVIDENCE_KINDS = Object.keys(EVIDENCE_PREFIX);

export const UID_RE = /^U[0-9]{1,15}$/i;
const NUM_RE = /^[1-9][0-9]{0,15}$/;
const EVIDENCE_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);
const EVIDENCE_MAX_BYTES = 8 << 20;
const EVIDENCE_MAX = 3;
const REFUND_ADDR_RE = /^(?:T[1-9A-HJ-NP-Za-km-z]{33}|0x[0-9a-fA-F]{40})$/;
const idOK = (v: string): boolean => NUM_RE.test(v) && Number.isSafeInteger(Number(v));

/** 与后端 mask.Email 同口径：local 前 2 字符 + *** + @域名；无 @ → ***。 */
export function maskEmail(e: string | null | undefined): string {
  if (!e) return '';
  const at = e.lastIndexOf('@');
  if (at <= 0) return '***';
  return [...e.slice(0, at)].slice(0, 2).join('') + '***' + e.slice(at);
}

/** 用户摘要（服务端 UserBrief，或由后台用户接口行换算）。 */
export type OwnerBrief = { id: number; uid: string; email_masked: string | null; telegram_username: string | null; status: string; merged_into_uid?: string | null };

/** 「U100005 · ab***@x.com / @tg」；非 active 附状态。 */
export function ownerLine(c: AdminCtx, b: OwnerBrief | null | undefined, fallbackId?: number | null): string {
  if (!b) return fallbackId ? `#${fallbackId}` : '—';
  const st = b.status !== 'active' ? `（${rText(c, `cpUserSt_${b.status}`)}${b.merged_into_uid ? ' → ' + b.merged_into_uid : ''}）` : '';
  return `${b.uid} · ${b.email_masked || rText(c, 'cpNoEmail')} / ${b.telegram_username ? '@' + b.telegram_username : rText(c, 'cpNoTg')}${st}`;
}

type UserListRow = { id: number; uid: string; email: string | null; telegram_username: string | null; status: string; merged_into_uid: string | null };
type LookupState = '' | 'ok' | 'inactive' | 'missing' | 'noperm' | 'bad';

/** 输入 → 查询后台用户（UID 走列表 keyword 精确匹配 uid；纯数字走 360 概览）；无 users.view 权限时降级为服务器校验。 */
export async function lookupOwner(c: AdminCtx, raw: string): Promise<{ state: LookupState; brief: OwnerBrief | null }> {
  const v = raw.trim();
  try {
    if (UID_RE.test(v)) {
      const uid = v.toUpperCase();
      const page = await c.gp.api.call<{ list: UserListRow[] }>('GET /api/admin/users', { query: { keyword: uid, size: 10 } });
      const hit = page.list.find((x) => x.uid === uid);
      return hit ? toBrief(hit) : { state: 'missing', brief: null };
    }
    if (idOK(v)) {
      const ov = await c.gp.api.call<UserListRow>('GET /api/admin/users/{id}/360', { path: { id: Number(v) }, query: { tab: 'overview' } });
      return toBrief(ov);
    }
    return { state: 'bad', brief: null };
  } catch (e) {
    if (e instanceof ApiError && e.code === 4040) return { state: 'missing', brief: null };
    if (e instanceof ApiError && (e.code === 1501 || e.status === 403)) return { state: 'noperm', brief: null };
    throw e;
  }
}
function toBrief(x: UserListRow): { state: LookupState; brief: OwnerBrief } {
  const brief: OwnerBrief = { id: x.id, uid: x.uid, email_masked: x.email ? maskEmail(x.email) : null, telegram_username: x.telegram_username, status: x.status, merged_into_uid: x.merged_into_uid };
  return { state: x.status === 'active' ? 'ok' : 'inactive', brief };
}

/** 隐藏键（不在 fields 中，随表单值保存）。 */
export const H = { state: '__owner_state', uid: '__owner_uid', id: '__owner_id' } as const;

/** 处置表单字段联动：入账用户输入 → 预览 / 红条。 */
export function remedyOnChange(c: AdminCtx, exceptionId: number, k: string, v: FormVals): FormChange | Promise<FormChange> | void {
  if (k !== 'user_uid') return;
  const raw = String(v['user_uid'] ?? '').trim();
  void exceptionId;
  if (!raw) return { vals: { user_preview: '', [H.state]: '', [H.uid]: '', [H.id]: '' }, err: '' };
  if (!UID_RE.test(raw) && !idOK(raw)) return { vals: { user_preview: '', [H.state]: 'bad', [H.uid]: '', [H.id]: '' }, err: '' };
  return lookupOwner(c, raw).then(({ state, brief }): FormChange => {
    const base: FormVals = { [H.state]: state, [H.uid]: brief?.uid ?? '', [H.id]: brief ? String(brief.id) : '' };
    if (state === 'noperm') return { vals: { ...base, user_preview: rText(c, 'cpOwnerNoPerm') }, err: '' };
    if (state === 'missing') return { vals: { ...base, user_preview: '' }, err: fill(rText(c, 'cpErrOwnerMissing'), { uid: raw.toUpperCase() }) };
    if (state === 'inactive') return { vals: { ...base, user_preview: ownerLine(c, brief) }, err: fill(rText(c, 'cpErrOwnerInactive'), { uid: brief?.uid ?? raw }) };
    return { vals: { ...base, user_preview: ownerLine(c, brief) }, err: '' };
  });
}

/** 逐项校验（返回首个问题的具体文案；全部通过返回 ''）。 */
export function validateRemedy(c: AdminCtx, v: FormVals): string {
  const a = String(v['action'] ?? '');
  const owner = String(v['user_uid'] ?? '').trim();
  if (a === 'credit' || a === 'supplement') {
    if (!owner) return rText(c, 'cpErrOwnerEmpty');
    if (!UID_RE.test(owner) && !idOK(owner)) return rText(c, 'cpErrOwnerFormat');
    if (v[H.state] === 'missing') return fill(rText(c, 'cpErrOwnerMissing'), { uid: owner.toUpperCase() });
    if (v[H.state] === 'inactive') return fill(rText(c, 'cpErrOwnerInactive'), { uid: String(v[H.uid] || owner) });
    const order = String(v['order_id'] ?? '').trim();
    if (order && !idOK(order)) return rText(c, 'cpErrOrderId');
    if (a === 'supplement' && !idOK(String(v['remedy_group_id'] ?? '').trim())) return rText(c, 'cpErrGroupId');
  }
  if (a === 'refund' && !REFUND_ADDR_RE.test(String(v['refund_address'] ?? '').trim())) return rText(c, 'cpErrRefundAddr');
  if (evidenceRefsOf(v).length === 0) return rText(c, 'cpErrEvidenceEmpty');
  const note = String(v['note'] ?? '').trim();
  if (!note) return rText(c, 'cpErrNote');
  if (note.length > 1000) return rText(c, 'cpErrNoteLong');
  return '';
}

/** 请求体中的入账用户：UID 优先（user_uid）；纯数字已预览出 UID 时也转为 user_uid，否则按内部 id（user_id）。 */
export function ownerBody(v: FormVals): { user_uid: string } | { user_id: number } {
  const raw = String(v['user_uid'] ?? '').trim();
  if (UID_RE.test(raw)) return { user_uid: raw.toUpperCase() };
  const resolved = String(v[H.uid] ?? '');
  return resolved ? { user_uid: resolved } : { user_id: Number(raw) };
}

/** 处置建议接口拒绝原因 → 本页文案键（'' = 交给 cryptoErr 其他分支）。 */
export function remedyErrText(c: AdminCtx, e: ApiError): string {
  const reason = e.data.reason ?? '';
  if (e.code === 1012 && reason === 'user_not_found') return rText(c, 'cpErrOwnerMissingSrv');
  if (e.code === 1012 && reason === 'user_not_active') return rText(c, 'cpErrOwnerInactiveSrv');
  if (e.code === 4220 && e.data.field === 'user_uid') return rText(c, 'cpErrOwnerConflict');
  if (e.code === 4220 && e.data.field === 'user_id') return rText(c, 'cpErrOwnerFormat');
  if (e.code === 4220 && e.data.field === 'order_id') return rText(c, 'cpErrOrderOwner');
  if (e.code === 4220 && e.data.field === 'remedy_group_id') return rText(c, 'cpErrGroupId');
  if (e.code === 4220 && e.data.field === 'evidence_refs') return rText(c, 'cpErrEvidenceRef');
  if (e.code === 1012 && reason === 'evidence_file_not_owner') return rText(c, 'cpErrEvidenceOwner');
  if (e.code === 1012 && reason === 'evidence_file_reused') return rText(c, 'cpErrEvidenceReused');
  if (e.code === 4220 && e.data.field === 'refund_address') return rText(c, 'cpErrRefundAddr');
  return '';
}

// ---- 凭证图片（迁移 0042）----

/** 上传 / 详情中的凭证行（服务端 cryptopay.EvidenceFile）。 */
export type EvidenceFile = { id: number; ref: string; content_type: string; size_bytes: number; sha256: string; created_at: string };

/** 表单值 evidence_files：已上传引用（file-00000012）逗号分隔。 */
export const evidenceRefsOf = (v: FormVals): string[] => String(v['evidence_files'] ?? '').split(',').map((x) => x.trim()).filter(Boolean);
const kb = (n: number): string => `${Math.max(1, Math.round(n / 1024))}KB`;
export const evidenceSummary = (c: AdminCtx, list: readonly EvidenceFile[]): string =>
  list.length ? fill(rText(c, 'cpEvidenceUploaded'), { n: String(list.length), list: list.map((f) => `#${f.id}（${f.content_type.replace('image/', '')} ${kb(f.size_bytes)}）`).join('、') }) : rText(c, 'cpEvidenceNone');

/** file 字段选中：本地预检（类型 / 8MB / ≤ 3 张）→ 逐张 POST /api/admin/crypto-evidence-files；重新选择替换已选。 */
export async function uploadEvidenceFiles(c: AdminCtx, files: readonly File[]): Promise<FormChange> {
  if (files.length > EVIDENCE_MAX) return { err: rText(c, 'cpErrEvidenceTooMany') };
  for (const f of files) {
    if (!EVIDENCE_MIME.has(f.type)) return { err: fill(rText(c, 'cpErrEvidenceType'), { name: f.name }) };
    if (f.size > EVIDENCE_MAX_BYTES) return { err: fill(rText(c, 'cpErrEvidenceSize'), { name: f.name }) };
  }
  const out: EvidenceFile[] = [];
  for (const f of files) {
    const form = new FormData();
    form.append('file', f, f.name);
    try {
      out.push(await c.gp.api.call<EvidenceFile>('POST /api/admin/crypto-evidence-files', { form }));
    } catch (e) {
      if (e instanceof ApiError && e.code === 4220 && e.data.reason === 'file_too_large') return { err: fill(rText(c, 'cpErrEvidenceSize'), { name: f.name }) };
      if (e instanceof ApiError && e.code === 4220) return { err: fill(rText(c, 'cpErrEvidenceType'), { name: f.name }) };
      if (e instanceof ApiError && e.status === 413) return { err: fill(rText(c, 'cpErrEvidenceSize'), { name: f.name }) };
      if (e instanceof ApiError && (e.status === 429 || e.code === 1002)) return { err: rText(c, 'cpErrEvidenceBusy') };
      return { err: c.gp.ui.err(e) };
    }
  }
  return { vals: { evidence_files: out.map((f) => f.ref).join(','), evidence_list: evidenceSummary(c, out) }, err: '' };
}

// 本会话已查看的凭证 id（复核通过前须逐张查看）；随宿主回收
const viewed = new WeakMap<object, Set<number>>();
export const evidenceViewed = (c: AdminCtx, ids: readonly number[]): boolean => ids.every((id) => viewed.get(c.host)?.has(id) ?? false);

/** 新窗查看凭证图片（同 KYC：先开占位窗保住用户激活，再以 Bearer 取 blob，校验 MIME 后 objectURL 打开）。 */
export async function openEvidenceFile(c: AdminCtx, id: number): Promise<void> {
  const w = globalThis.open?.('about:blank', '_blank') ?? null;
  try {
    const { blob } = await c.gp.api.blob('GET /api/admin/files/crypto-evidence/{id}', { path: { id } });
    if (!EVIDENCE_MIME.has(blob.type)) throw new Error(`evidence mime not allowed: ${blob.type || '(empty)'}`);
    const set = viewed.get(c.host) ?? new Set<number>();
    set.add(id);
    viewed.set(c.host, set);
    if (typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(blob);
    if (w) {
      w.opener = null;
      w.location.replace(url);
    } else globalThis.open?.(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    w?.close();
    c.gp.ui.toast(c.gp.ui.err(e));
  }
}

/** GET /api/admin/crypto-exceptions/{id} candidate_orders[]（服务端 cryptopay.CandidateOrder）。 */
export type CandidateOrder = {
  id: number; user_id: number; uid: string; email_masked: string | null; telegram_username: string | null; user_status: string;
  network: string; address: string; amount_expected: string; amount_diff: string; status: string; created_at: string; expires_at: string; system_candidate: boolean;
};
export const candidateOwner = (o: CandidateOrder): OwnerBrief => ({ id: o.user_id, uid: o.uid, email_masked: o.email_masked, telegram_username: o.telegram_username, status: o.user_status });

/** 候选订单列表（抽屉 list）：pick 缺省（异常非待处理 / 无编辑权限）时只读。 */
export function candidateList(c: AdminCtx, list: readonly CandidateOrder[], pick?: (ctx: AdminCtx, o: CandidateOrder) => void): NonNullable<DrawerSpec['list']> {
  const st = (v: string): string => str(c.t, `cpSt_order_${v}`) || v;
  return {
    title: rText(c, 'cpCandTitle'),
    rows: list.map((o) => ({
      left: `#${o.id} · ${ownerLine(c, candidateOwner(o))}`,
      sub: `${fill(rText(c, 'cpCandDue'), { amount: o.amount_expected })} · ${fill(rText(c, 'cpCandCreated'), { t: fmtDateTime(o.created_at, { tz: c.s.sh.timezone || undefined, seconds: true }) })} · ${st(o.status)}`,
      right: fill(rText(c, 'cpCandDiff'), { diff: o.amount_diff }),
      rightSub: pick ? rText(c, o.system_candidate ? 'cpCandSystem' : 'cpCandPick') : rText(c, 'cpCandReadonly'),
      tone: o.amount_diff === '0' ? ('good' as const) : ('warn' as const),
      ...(pick ? { go: (ctx: AdminCtx) => pick(ctx, o) } : {}),
    })),
  };
}
